import { CreateServiceFormValues, FieldErrors } from './types';
import { ClientHierarchySelection } from './useClientHierarchy';
import { LocationSearchState } from './useLocationSearch';
import { ServiceCreationRuleTypes } from '../../../configs/enums';
import { EMPTY_FIELD_MESSAGE } from '../../../constants/common';
import { isEmptyString } from '../../../utils/common';
import { isRuleExist } from '../../../utils/services';

interface ValidateArgs {
  values: CreateServiceFormValues;
  hierarchy: ClientHierarchySelection;
  location: LocationSearchState;
  showOverride: boolean;
  selectedRules: string[];
  submittedFaultId: string;
  // Client/Customer users use a simplified Part Details section (part type only),
  // so the rule-driven serial / model / fault validations don't apply.
  isClientCustomerUser?: boolean;
}

const required = (label: string) => `${label}${EMPTY_FIELD_MESSAGE}`;

/** Strip HTML tags, decode non-visible whitespace entities, and check whether
 * any visible text content remains. Guards against Quill emitting e.g.
 * <p>&nbsp;</p> when the user hasn't typed anything. */
const isHtmlContentEmpty = (html: string): boolean => {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .replace(/[\u00A0\u200B]/g, '');
  return !text.trim();
};

const validateMapping = (
  values: CreateServiceFormValues,
  showOverride: boolean
): string | undefined => {
  if (!showOverride && isEmptyString(values.serviceTypeMapping)) return required('Service Type');
  if (
    showOverride &&
    (isEmptyString(values.serviceTypeCode) ||
      isEmptyString(values.actionTypeCode) ||
      isEmptyString(values.processTypeCode))
  ) {
    return required('Service Type');
  }
  return undefined;
};

const validateReferences = (
  values: CreateServiceFormValues,
  selectedRules: string[]
): Pick<FieldErrors, 'reference1' | 'reference2' | 'reference3'> => {
  const next: Pick<FieldErrors, 'reference1' | 'reference2' | 'reference3'> = {};
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1) &&
    isEmptyString(values.referenceNo1)
  )
    next.reference1 = required('Reference 1');
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2) &&
    isEmptyString(values.referenceNo2)
  )
    next.reference2 = required('Reference 2');
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3) &&
    isEmptyString(values.referenceNo3)
  )
    next.reference3 = required('Reference 3');
  return next;
};

const validateReportedInfo = (
  values: CreateServiceFormValues,
  selectedRules: string[],
  submittedFaultId: string
): Pick<FieldErrors, 'serialNumber' | 'model' | 'fault' | 'notes'> => {
  const next: Pick<FieldErrors, 'serialNumber' | 'model' | 'fault' | 'notes'> = {};
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber) &&
    isEmptyString(values.reportedSerialNumber)
  )
    next.serialNumber = required('Serial Number');
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Model) &&
    isEmptyString(values.reportedModel)
  )
    next.model = required('Model');
  if (isRuleExist(selectedRules, ServiceCreationRuleTypes.FaultAndDescription) && !submittedFaultId)
    next.fault = required('Fault');
  if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes) &&
    isHtmlContentEmpty(values.notes)
  )
    next.notes = required('Notes');
  return next;
};

export const validateCreateServiceForm = (args: ValidateArgs): FieldErrors => {
  const {
    values,
    hierarchy,
    location,
    showOverride,
    selectedRules,
    submittedFaultId,
    isClientCustomerUser,
  } = args;
  const errors: FieldErrors = {};
  if (!hierarchy.clientId) errors.hierarchy = required('Client');
  if (!location.mainLocationNode?.id) errors.location = required('Location');
  const mapping = validateMapping(values, showOverride);
  if (mapping) errors.mapping = mapping;
  if (isEmptyString(values.priorityCode)) errors.priority = required('Priority');
  Object.assign(errors, validateReferences(values, selectedRules));
  if (!isClientCustomerUser) {
    Object.assign(errors, validateReportedInfo(values, selectedRules, submittedFaultId));
  } else if (
    isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes) &&
    isHtmlContentEmpty(values.notes)
  ) {
    errors.notes = required('Notes');
  }
  return errors;
};
